import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        // Prevent caching of auth check
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('brifx_user', JSON.stringify(userData));
        return true;
      } else if (response.status === 401 || response.status === 403) {
        // Only clear on definitive auth failure (401/403)
        console.log('Session expired - logging out');
        setUser(null);
        localStorage.removeItem('brifx_user');
        return false;
      } else {
        // Server error (500, etc.) - keep user logged in, don't disrupt
        console.warn('Auth check returned non-OK status, keeping current session');
        return true;
      }
    } catch (error) {
      // Network error - keep user logged in, don't disrupt their work
      console.warn('Auth check failed (network), keeping current session:', error.message);
      return true;
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // Initial auth check
  useEffect(() => {
    const initAuth = async () => {
      // First try to restore from localStorage for instant UI
      const storedUser = localStorage.getItem('brifx_user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          localStorage.removeItem('brifx_user');
        }
      }
      
      // Then verify with server
      await checkAuth();
      setLoading(false);
    };
    
    initAuth();
  }, [checkAuth]);

  // Re-check auth when window regains focus (user comes back to tab)
  useEffect(() => {
    const handleFocus = () => {
      // Silently verify session is still valid
      checkAuth(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuth(false);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAuth]);

  // Periodic session validation (every 5 minutes)
  useEffect(() => {
    if (!user) return;
    
    const intervalId = setInterval(() => {
      checkAuth(false);
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(intervalId);
  }, [user, checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSession = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Authentication failed');
      }

      const userData = await response.json();
      setUser(userData);
      // Store user in localStorage as backup
      localStorage.setItem('brifx_user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('brifx_user');
    }
  };

  const setUserData = (userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('brifx_user', JSON.stringify(userData));
    }
  };

  // Function to handle 401 errors from any API call
  const handleAuthError = useCallback(() => {
    setUser(null);
    localStorage.removeItem('brifx_user');
    // Redirect to login
    window.location.href = '/';
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login,
    logout,
    processSession,
    setUserData,
    checkAuth,
    handleAuthError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
